import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from '../../controls/button/button.component';
import {ITransactionCheck} from './interface';
import {TransactionsService} from './transactions.service';
import {NotificationService} from '@notifications';
import {finalize} from 'rxjs/operators';
import {DatePickerComponent} from '@reusable/date-picker/date-picker.component';

@Component({
  selector: 'app-check-data-form',
  standalone: true,
  template: `
    <form [formGroup]="form" class="d-flex flex-column gap-2">
      <div class="input-group">
        <span class="input-group-text">Pay To</span>
        <input type="text" class="form-control" formControlName="ocrPayTo" />
      </div>
      <div class="input-group">
        <span class="input-group-text">Amount</span>
        <input type="number" class="form-control" formControlName="ocrAmount" step="0.01" />
      </div>
      <div class="input-group">
        <span class="input-group-text">Check Number</span>
        <input type="text" class="form-control" formControlName="ocrCheckNumber" />
      </div>
      <div class="input-group">
        <span class="input-group-text">Bank Name</span>
        <input type="text" class="form-control" formControlName="ocrBankName" />
      </div>
      <div class="input-group">
        <span class="input-group-text">Check Date</span>
        <app-date-picker
          formControlName="ocrCheckDate"
          placeholder="Select Check Date"
        ></app-date-picker>
      </div>
      <div class="input-group">
        <span class="input-group-text">Memo</span>
        <input type="text" class="form-control" formControlName="ocrMemo" />
      </div>
      <app-button
        [loading]="loading"
        [disabled]="loading || form.invalid"
        (click)="onUpdate()"
      >Update</app-button>
    </form>
  `,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    DatePickerComponent,
  ],
})
export class CheckDataFormComponent implements OnInit, OnChanges {
  @Input() checkData: ITransactionCheck;
  @Output() updated = new EventEmitter<ITransactionCheck>();

  form: FormGroup;
  loading: boolean = false;

  private formBuilder: FormBuilder = inject(FormBuilder);
  private transactionsService: TransactionsService = inject(TransactionsService);
  private notificationService: NotificationService = inject(NotificationService);

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['checkData'] && this.form) {
      this.initForm();
    }
  }

  initForm() {
    // Convert string date to Date object for the date picker
    const checkDate = this.checkData?.ocrCheckDate 
      ? new Date(this.checkData.ocrCheckDate) 
      : null;
    
    this.form = this.formBuilder.group({
      ocrPayTo: [this.checkData?.ocrPayTo || ''],
      ocrAmount: [this.checkData?.ocrAmount || 0],
      ocrCheckNumber: [this.checkData?.ocrCheckNumber || ''],
      ocrBankName: [this.checkData?.ocrBankName || ''],
      ocrCheckDate: [checkDate],
      ocrMemo: [this.checkData?.ocrMemo || ''],
    });
  }

  onUpdate() {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.value;
      
      // Convert Date object to string format for the API
      const updateData: ITransactionCheck = {
        id: this.checkData.id,
        qboTransactionId: this.checkData.qboTransactionId,
        checkImageId: this.checkData.checkImageId,
        storageUri: this.checkData.storageUri,
        ocrPayTo: formValue.ocrPayTo,
        ocrAmount: formValue.ocrAmount,
        ocrCheckNumber: formValue.ocrCheckNumber,
        ocrBankName: formValue.ocrBankName,
        ocrCheckDate: formValue.ocrCheckDate,
        ocrMemo: formValue.ocrMemo,
      };

      this.transactionsService.updateCheckOcr(updateData)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: () => {
          this.notificationService.success('Check OCR data updated successfully');
          this.updated.emit(updateData);
        },
        error: (error) => {
          this.notificationService.error(error);
        },
      });
    }
  }
}
